{
  "slug": "general-intelligence-g-factor-explained",
  "title": "The General Intelligence Factor (g) Explained",
  "description": "Understand what psychologists mean by the general intelligence factor, how factor analysis identifies it, and what g does not imply.",
  "summary": "The general intelligence factor, or g, is a statistical dimension that summarizes what diverse cognitive tests tend to share. It is influential, useful, and still debated in its interpretation.",
  "category": "Science of testing",
  "sections": [
    {
      "heading": "What the g factor means",
      "paragraphs": [
        "People who perform relatively well on one demanding cognitive task tend, on average, to perform relatively well on other kinds of cognitive tasks. Psychologists call this pattern the positive manifold. The general factor, usually written as g, is a statistical way of representing that shared variation.",
        "Charles Spearman introduced an early formal account in 1904 after examining correlations among school and sensory measures. Modern models use larger batteries and more advanced factor analysis, but the central observation remains: scores across reasoning, knowledge, memory, spatial, and speed tasks are usually positively correlated.",
        "The g factor is not a single test item, brain region, or substance that can be directly observed. It is inferred from patterns of performance, much as other latent psychological constructs are inferred from multiple indicators."
      ]
    },
    {
      "heading": "How factor analysis finds shared variance",
      "paragraphs": [
        "Factor analysis examines correlations among measured variables. If many tests move together, a model can estimate a common dimension that accounts for part of those relationships. Each test can have a loading indicating how strongly it relates to that dimension, while still retaining task-specific variance and measurement error.",
        "Results depend on the tests, people, statistical model, and decisions made by researchers. A battery containing mostly verbal tasks may produce a different structure from one sampling verbal, spatial, memory, and speed abilities broadly. That is why g should not be treated as independent of measurement design.",
        "Different factor models can sometimes fit the same data reasonably well. Researchers debate whether g is best viewed as a causal mental capacity, an emergent property of interacting abilities, a formative summary, or mainly a useful statistical regularity."
      ]
    },
    {
      "heading": "How g relates to an IQ score",
      "paragraphs": [
        "A broad full-scale IQ composite often correlates strongly with g because it combines performance across several cognitive domains. The terms are not exact synonyms. IQ is a reported score from a particular instrument and norm group; g is a latent factor estimated from relationships among tests.",
        "Two batteries can both report full-scale IQ while sampling somewhat different abilities. A composite may also include variance from broad abilities, specific task demands, and measurement error. An individual's profile can therefore contain meaningful strengths and weaknesses even when one overall score is available.",
        "A high g loading does not make a task pure. Vocabulary depends on acquired knowledge and language experience; matrix reasoning depends on visual access, instructions, strategy, and familiarity as well as shared reasoning demands."
      ]
    },
    {
      "heading": "Broad and narrow abilities still matter",
      "paragraphs": [
        "Hierarchical theories place broad abilities below a general factor and narrower skills below those. Common broad domains include fluid reasoning, acquired knowledge, visual processing, working memory, and processing speed. These domains are correlated but not interchangeable.",
        "For research questions, g can summarize a large correlation matrix efficiently. For an individual assessment, the referral question may require attention to the pattern, confidence intervals, behavior during testing, educational history, language, sensory or motor access, and real-world functioning.",
        "Interpreting every subtest difference as a stable personal trait is also a mistake. Subscores are often less reliable than broad composites, and an unusual difference can arise by chance when many comparisons are made."
      ]
    },
    {
      "heading": "What g can and cannot establish",
      "paragraphs": [
        "General cognitive ability is associated at the group level with learning and performance in many settings. Associations do not make outcomes inevitable for an individual. Motivation, opportunity, health, personality, education, social conditions, domain knowledge, and chance also matter.",
        "The existence of a positive manifold does not show that intelligence is fixed, entirely genetic, culturally neutral, or a complete account of human capability. It does not directly measure creativity, wisdom, moral judgment, practical expertise, or a person's value.",
        "Responsible use treats g as one evidence-based model of covariance among cognitive tasks. Claims about causes, biology, group differences, or policy require additional evidence and careful attention to sampling, fairness, and uncertainty."
      ]
    }
  ],
  "faqs": [
    {
      "question": "Is g the same thing as IQ?",
      "answer": "Not exactly. g is a latent statistical factor; IQ is a score reported by a particular standardized test. Broad IQ composites are often strong indicators of g."
    },
    {
      "question": "Does every cognitive test measure g?",
      "answer": "Many cognitively demanding tests correlate with g to some degree, but their loadings differ and every task also reflects specific demands and measurement error."
    },
    {
      "question": "Is the g factor universally accepted?",
      "answer": "The positive correlations among cognitive tests are well established, but researchers disagree about the best model and the psychological or biological interpretation of the general factor."
    },
    {
      "question": "Can one subtest provide a g score?",
      "answer": "A single task can correlate with g, but estimating a broad general factor is stronger when multiple, varied, well-standardized indicators are used."
    }
  ],
  "related": [
    "iq-vs-intelligence",
    "fluid-intelligence",
    "crystallized-intelligence",
    "how-is-iq-measured"
  ],
  "sources": [
    {
      "label": "Spearman (1904) - General Intelligence Objectively Determined and Measured",
      "url": "https://psychclassics.yorku.ca/Spearman/"
    },
    {
      "label": "Cambridge Handbook of Intelligence - Factor-Analytic Models",
      "url": "https://doi.org/10.1017/CBO9780511977244.004"
    },
    {
      "label": "Carroll's Three-Stratum Cognitive Ability Theory at 30 Years",
      "url": "https://pmc.ncbi.nlm.nih.gov/articles/PMC9959556/"
    },
    {
      "label": "Standards for Educational and Psychological Testing",
      "url": "https://www.testingstandards.net/"
    }
  ]
}
